if (!customElements.get('product-quick-add')) {
  customElements.define('product-quick-add', class ProductQuickAdd extends HTMLElement {
    constructor() {
      super();
      
      this.variantId = this.dataset.variantId;
      this.productId = this.dataset.productId;
      this.formId = `quick-add-form-${this.productId}`;

      this.trigger = this.querySelector('.quick-add__trigger');
      this.panel = this.querySelector('.quick-add__panel');
      this.quantityComponent = this.panel ? this.panel.querySelector('quantity-input') : null;
      this.quantityInput = this.panel ? this.panel.querySelector('input[name="quantity"]') : null;
      this.triggerLabel = this.dataset.triggerLabel;

      if (!this.panel || !this.quantityInput || !this.variantId) return;

      this.initialQuantity = parseInt(this.dataset.initialQuantity || '0', 10);

      if (this.quantityComponent && !this.quantityComponent.hasAttribute('form')) {
        const input = this.quantityComponent.querySelector('input');
        if (input) input.setAttribute('form', this.formId);
      }

      if (this.initialQuantity > 0) {
        this.panel.classList.add('is-open');
        this.quantityInput.value = this.initialQuantity;
        if (this.trigger) this.trigger.classList.add('hide');
      }

      if (this.trigger) {
        this.trigger.addEventListener('click', (event) => this.handleTriggerClick(event));
      }

      this.addEventListener('mousedown', (event) => event.stopPropagation());
      this.addEventListener('mouseup', (event) => event.stopPropagation());
      this.addEventListener('touchstart', (event) => event.stopPropagation());

      if (this.quantityComponent) {
        this.quantityComponent.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
      }

      this.quantityInput.addEventListener('change', (event) => this.handleInputChange(event));
      this.quantityInput.addEventListener('click', (event) => event.stopPropagation());
    }

    handleTriggerClick(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!this.panel.classList.contains('is-open')) {
        this.panel.classList.add('is-open');
        this.quantityInput.value = 1;
        this.submit();

        if (this.trigger) this.trigger.classList.add('hide');
        this.blurAnchor();
      }
    }

    handleInputChange(event) {
      const value = parseInt(event.target.value, 10);
      if (Number.isNaN(value) || value < 0) {
        event.target.value = this.initialQuantity || 0;
        return;
      }

      if (this.quantityComponent && event.target !== this.quantityInput) {
        this.quantityInput.value = value;
      }

      this.quantityInput.value = value;
      const displayValue = this.quantityComponent ? this.quantityComponent.querySelector('.quantity__value') : null;
      if (displayValue) displayValue.textContent = value;
      this.submit();
    }

    syncFromCart(quantity) {
      const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
      this.initialQuantity = safeQuantity;

      if (this.quantityComponent) {
        this.quantityComponent.classList.remove('is-loading');
        const input = this.quantityComponent.querySelector('input');
        const displayValue = this.quantityComponent.querySelector('.quantity__value');
        const normalizedValue = safeQuantity || (safeQuantity === 0 ? 1 : safeQuantity);

        if (input) {
          input.value = normalizedValue;
        }

        if (displayValue) {
          displayValue.textContent = normalizedValue;
        }

        this.quantityComponent.querySelectorAll('button').forEach((button) => {
          button.toggleAttribute('disabled', false);
        });
      }

      if (this.quantityInput) {
        const normalizedValue = safeQuantity || (safeQuantity === 0 ? 1 : safeQuantity);
        this.quantityInput.value = normalizedValue;
        this.quantityInput.toggleAttribute('disabled', false);
      }

      if (safeQuantity > 0) {
        this.panel.classList.add('is-open');
        if (this.trigger) this.trigger.classList.add('hide');
      } else {
        this.panel.classList.remove('is-open');
        if (this.trigger) this.trigger.classList.remove('hide');
      }
    }

    submit() {
      const quantity = parseInt(this.quantityInput.value, 10) || 0;
      const variantIdNum = parseInt(this.variantId, 10);
      if (!variantIdNum) return;

      const previousQuantity = this.initialQuantity;
      const isPreorder = this.dataset.isPreorder === 'true';
      this.setLoading(true);

      const isRemoving = quantity === 0 || quantity < previousQuantity;
      const updateBody = isRemoving 
        ? { updates: { [variantIdNum]: quantity } }
        : { 
            id: variantIdNum, 
            quantity: quantity - previousQuantity,
            ...(isPreorder && { properties: { _preorder: 'true' } })
          };

      const url = isRemoving ? window.routes.cart_update_url + '.js' : window.routes.cart_add_url + '.js';
      
      fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Accept: 'application/json' 
        },
        body: JSON.stringify(updateBody)
      })
        .then(async (response) => {
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data?.description || 'Unable to update cart');
          }

          let cart;
          
          if (isRemoving) {
            // When removing, use cart data from response
            cart = data;
          } else {
            // When adding, update cart attributes and fetch cart in parallel if preorder
            const promises = [];
            if (isPreorder) {
              promises.push(
                fetch(window.routes.cart_update_url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ attributes: { _preorder: 'true' } })
                }).catch(err => console.error('Error updating preorder attribute:', err))
              );
            }
            promises.push(fetch('/cart.js').then(r => r.json()));
            cart = (await Promise.all(promises))[promises.length - 1];
          }

          this.initialQuantity = quantity;
          const displayValue = this.quantityComponent?.querySelector('.quantity__value');
          if (displayValue) displayValue.textContent = quantity;

          const matchedCartItem = cart?.items?.find((item) => Number(item.variant_id) === variantIdNum);
          const detailItem = {
            variantId: variantIdNum,
            productId: matchedCartItem?.product_id,
            quantity: matchedCartItem?.quantity ?? quantity,
            previousQuantity,
            productTitle: matchedCartItem?.product_title || matchedCartItem?.title,
            title: matchedCartItem?.title
          };

          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { 
            detail: { cart: cart, items: [detailItem] } 
          }));

          if (quantity === 0) {
            this.panel.classList.remove('is-open');
            if (this.trigger) this.trigger.classList.remove('hide');
          }
        })
        .catch((error) => {
          console.error('Quick add failed:', error);
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    setLoading(isLoading) {
      if (this.panel) {
        this.panel.classList.toggle('is-loading', isLoading);
      }
      
      if (this.quantityComponent) {
        this.quantityComponent.classList.toggle('is-loading', isLoading);
        this.quantityComponent.querySelectorAll('button').forEach((button) => {
          button.toggleAttribute('disabled', isLoading);
        });
      }

      if (this.quantityInput) {
        this.quantityInput.toggleAttribute('disabled', isLoading);
      }
      
      this.toggleInlineLoading(isLoading);
    }

    toggleInlineLoading(show) {
      if (!this.quantityComponent) return;
      const quantityInput = this.quantityComponent.querySelector('.quantity__input');
      const displayValue = this.quantityComponent.querySelector('.quantity__value');
      const loadingOverlay = this.quantityComponent.querySelector('.loading-overlay');

      if (quantityInput) {
        quantityInput.classList.toggle('hidden', show);
      }

      if (displayValue) {
        displayValue.classList.toggle('hidden', show);
      }

      if (loadingOverlay) {
        loadingOverlay.classList.toggle('hidden', !show);
      }
    }

    blurAnchor() {
      const anchor = this.closest('.card-media');
      if (anchor) {
        anchor.classList.add('is-interacting');
        requestAnimationFrame(() => anchor.classList.remove('is-interacting'));
      }
    }
  });
}

